import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import SectionWrapper from '../../../components/SectionWrapper/SectionWrapper';
import BasicPagination from '../../../components/DataTable/BasicPagination';

import useRestaurantPurchaseOrders from '../../../hooks/restaurant/useRestaurantPurchaseOrders';
import useDebounced from '../../../hooks/useDebounced';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';
import useBranchScope from '../../../hooks/branches/useBranchScope';
import { RestaurantPurchaseOrderFilters } from '../../../types/restaurant/RestaurantPurchaseOrderTypes';

import RestaurantPurchaseOrderDetailModal from './RestaurantPurchaseOrderDetailModal';
import RestaurantPurchaseOrderFormModal from './RestaurantPurchaseOrderFormModal';
import DtePurchaseImportModal from './dteImport/DtePurchaseImportModal';
import PurchaseHeader from './components/PurchaseHeader';
import PurchaseFilters from './components/PurchaseFilters';
import PurchaseTable from './components/PurchaseTable';
import PurchaseList from './components/PurchaseList';
import PurchaseBranchEmpty from './components/PurchaseBranchEmpty';
import PurchaseSummary from './components/PurchaseSummary';
import PurchaseSkeleton from './components/PurchaseSkeleton';
import PurchaseEmpty from './components/PurchaseEmpty';
import { PurchaseStatusFilter } from './types';

const RestaurantPurchasesScreen = () => {
  // =====================================================
  // 🔹 Sucursales
  // =====================================================

  const {
    branches,
    branchId,
    setBranchId,
    loadingBranches,
    branchError,
    canPickBranch,
  } = useBranchScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const openedPurchaseId = useRef<number | null>(null);

  // =====================================================
  // 🔹 Filtros
  // =====================================================

  const [search, setSearch] = useState('');

  const [statusFilter, setStatusFilter] =
    useState<PurchaseStatusFilter>('ALL');

  const [supplierId, setSupplierId] = useState(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [importOpen, setImportOpen] = useState(false);

  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const purchases = useRestaurantPurchaseOrders(branchId);

  const debouncedSearch = useDebounced(search, 350);

  const normalizedSearch =
    search === '' ? '' : debouncedSearch;

  // =====================================================
  // 🔹 Cargar sucursales
  // =====================================================

  // =====================================================
  // 🔹 Filtros para backend
  // =====================================================

  const filters = useMemo<RestaurantPurchaseOrderFilters>(
    () => ({
      search: normalizedSearch.trim() || undefined,

      status:
        statusFilter === 'ALL'
          ? undefined
          : statusFilter,

      supplierId: supplierId || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      page,
      size,
    }),
    [
      dateFrom,
      dateTo,
      normalizedSearch,
      page,
      size,
      statusFilter,
      supplierId,
    ]
  );

  // =====================================================
  // 🔹 Cargar opciones
  // =====================================================

  useEffect(() => {
    if (branchId) {
      purchases.loadOptions();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  // =====================================================
  // 🔹 Cargar compras
  // =====================================================

  useEffect(() => {
    if (branchId) {
      purchases.load(filters);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, filters]);

  useEffect(() => {
    const purchaseId = Number(searchParams.get('purchaseId'));
    if (!branchId || !purchaseId || openedPurchaseId.current === purchaseId) {
      return;
    }

    openedPurchaseId.current = purchaseId;
    purchases.view({ id: purchaseId }).finally(() => {
      const next = new URLSearchParams(searchParams);
      next.delete('purchaseId');
      setSearchParams(next, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, searchParams]);

  // =====================================================
  // 🔹 Eventos
  // =====================================================

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('ALL');
    setSupplierId(0);
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const changeBranch = (nextBranchId: number) => {
    resetFilters();
    setBranchId(nextBranchId);
  };

  const clearDates = () => {
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  // =====================================================
  // 🔹 Indicadores
  // =====================================================

  const draftCount = purchases.list.filter(
    (purchase) => purchase.status === 'DRAFT'
  ).length;

  const confirmedTotal = purchases.list
    .filter(
      (purchase) => purchase.status === 'CONFIRMED'
    )
    .reduce(
      (total, purchase) =>
        total + Number(purchase.total),
      0
    );

  // Ya no exige materias primas cargadas: se pueden crear al vuelo desde el modal.
  const createDisabled =
    !branchId ||
    purchases.loadingOptions ||
    purchases.suppliers.length === 0;

  // =====================================================
  // 🔹 Sin sucursales
  // =====================================================

  if (!loadingBranches && !branchError && !branchId) {
    return (
      <WorkspaceLayout>
        <BreadcrumbTwo />

        <SectionWrapper>
          <PurchaseBranchEmpty />
        </SectionWrapper>
      </WorkspaceLayout>
    );
  }

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <PurchaseHeader
          branches={branches}
          branchId={branchId}
          loadingBranches={loadingBranches}
          branchError={branchError}
          canPickBranch={canPickBranch}
          createDisabled={createDisabled}
          onBranchChange={changeBranch}
          onCreate={purchases.openCreate}
          onImport={() => setImportOpen(true)}
        />

        {branchId && (
          <>
            <PurchaseSummary
              totalCount={purchases.page?.totalElements ?? purchases.list.length}
              draftCount={draftCount}
              confirmedTotal={confirmedTotal}
            />

            <PurchaseFilters
              search={search}
              statusFilter={statusFilter}
              supplierId={supplierId}
              dateFrom={dateFrom}
              dateTo={dateTo}
              suppliers={purchases.suppliers}
              onSearchChange={(value) => { setSearch(value); setPage(0); }}
              onStatusChange={(value) => { setStatusFilter(value); setPage(0); }}
              onSupplierChange={(value) => { setSupplierId(value); setPage(0); }}
              onDateFromChange={(value) => { setDateFrom(value); setPage(0); }}
              onDateToChange={(value) => { setDateTo(value); setPage(0); }}
              onClearDates={clearDates}
            />

            {purchases.loading ? (
              <PurchaseSkeleton />
            ) : purchases.list.length === 0 ? (
              <PurchaseEmpty />
            ) : (
              <div className="relative">
                <PurchaseTable
                  purchases={purchases.list}
                  actingId={purchases.actingId}
                  onView={purchases.view}
                  onEdit={purchases.edit}
                  onConfirm={(purchase) =>
                    purchases.confirm(
                      purchase,
                      filters
                    )
                  }
                  onCancel={(purchase) =>
                    purchases.cancel(
                      purchase,
                      filters
                    )
                  }
                />

                <PurchaseList
                  purchases={purchases.list}
                  actingId={purchases.actingId}
                  onView={purchases.view}
                  onEdit={purchases.edit}
                  onConfirm={(purchase) =>
                    purchases.confirm(
                      purchase,
                      filters
                    )
                  }
                  onCancel={(purchase) =>
                    purchases.cancel(
                      purchase,
                      filters
                    )
                  }
                />
              </div>
            )}

            {purchases.page && (
              <BasicPagination
                currentPage={purchases.page.number}
                totalPages={purchases.page.totalPages}
                totalElements={purchases.page.totalElements}
                size={purchases.page.size}
                first={purchases.page.number === 0}
                last={purchases.page.number >= purchases.page.totalPages - 1}
                onPageChange={setPage}
                onSizeChange={(value) => { setSize(value); setPage(0); }}
              />
            )}
          </>
        )}
      </SectionWrapper>

      <RestaurantPurchaseOrderFormModal
        isOpen={purchases.formOpen}
        form={purchases.form}
        suppliers={purchases.suppliers}
        rawMaterials={purchases.rawMaterials}
        categories={purchases.categories}
        units={purchases.units}
        conversions={purchases.conversions}
        paymentConditions={purchases.paymentConditions}
        terms={purchases.terms}
        saving={purchases.saving}
        loadingOptions={purchases.loadingOptions}
        onCreateRawMaterial={purchases.createRawMaterialInline}
        onCreateCategory={purchases.createCategoryInline}
        onReloadSuppliers={purchases.reloadSuppliers}
        onClose={purchases.closeForm}
        onChange={purchases.updateField}
        onDetailChange={purchases.updateDetail}
        onAddDetail={purchases.addDetail}
        onRemoveDetail={purchases.removeDetail}
        onSave={() => purchases.save(filters)}
      />

      <RestaurantPurchaseOrderDetailModal
        isOpen={purchases.detailOpen}
        purchase={purchases.selected}
        onClose={purchases.closeDetail}
      />

      {branchId && (
        <DtePurchaseImportModal
          isOpen={importOpen}
          branchId={branchId}
          rawMaterials={purchases.rawMaterials}
          categories={purchases.categories}
          units={purchases.units}
          conversions={purchases.conversions}
          paymentConditions={purchases.paymentConditions}
          savingRawMaterial={purchases.saving}
          onCreateRawMaterial={purchases.createRawMaterialInline}
          onCreateCategory={purchases.createCategoryInline}
          onReloadSuppliers={purchases.reloadSuppliers}
          onReloadConversions={purchases.reloadConversions}
          onClose={() => setImportOpen(false)}
          onImported={async (form) => {
            setImportOpen(false);
            // Trae las equivalencias creadas en el asistente para que el
            // formulario de compra las tenga al abrir.
            await purchases.reloadConversions();
            purchases.openImported(form);
          }}
        />
      )}
    </WorkspaceLayout>
  );
};

export default RestaurantPurchasesScreen;
